import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  SERVER_FILE_SYSTEM,
  getStage3ServerProofPaths,
  getStage3ServerVoteThresholds,
} from "../../../../data/origami/serverFileSystem";

const VISIBLE_LINES = 16;

function getColorForVariant(v) {
  const colors = ["#22d3ee", "#d946ef", "#fbbf24", "#34d399", "#818cf8", "#f97316"];
  return colors[(Number(v) - 1) % colors.length];
}

export default function ServerTerminal({
  usbEjected,
  myVariant,
  playerCount,
  serverState,
  voteState,
  onAction,
  onDebugForceUpload,
}) {
  const [pendingAction, setPendingAction] = useState(null);
  const [voteFlash, setVoteFlash] = useState(null);

  const usbInserted = serverState?.usbInserted === true;
  const currentPath = serverState?.currentPath || "/";
  const cursorIndex = serverState?.cursorIndex ?? 0;
  const uploadedPaths = serverState?.uploadedPaths || [];
  const uploadedSet = new Set(uploadedPaths);
  const allUploaded = serverState?.allUploaded === true;
  const clipboard = serverState?.clipboard || null;

  const currentFolder = SERVER_FILE_SYSTEM[currentPath];
  const childNames = useMemo(() => currentFolder?.children || [], [currentPath, uploadedPaths]);

  const proofPaths = useMemo(() => getStage3ServerProofPaths(playerCount), [playerCount]);
  const voteThresholds = useMemo(() => getStage3ServerVoteThresholds(playerCount), [playerCount]);

  const children = useMemo(() => {
    let names = [...(currentFolder?.children || [])];
    if (currentPath === "/") names = names.filter((n) => n !== "USB_DEVICE" || usbInserted);
    if (currentPath === "/USB_DEVICE") names = proofPaths.map((p) => p.split("/").pop());
    if (currentPath === "/CONFERENCE") names = uploadedPaths.map((p) => p.split("/").pop());
    return names.map((name) => {
      const childPath = currentPath === "/" ? `/${name}` : `${currentPath}/${name}`;
      const item = SERVER_FILE_SYSTEM[childPath] || SERVER_FILE_SYSTEM[`/USB_DEVICE/${name}`];
      return item ? { ...item, path: childPath, name } : null;
    }).filter(Boolean);
  }, [currentPath, currentFolder, uploadedPaths, usbInserted, proofPaths]);

  const canGoUp = currentPath !== "/";
  const selectedItem = children[cursorIndex];
  const voteProgress = voteState
    ? Math.round((voteState.voters.length / Math.min(voteState.threshold, playerCount)) * 100)
    : 0;

  useEffect(() => {
    if (!voteFlash) return;
    const t = setTimeout(() => setVoteFlash(null), 800);
    return () => clearTimeout(t);
  }, [voteFlash]);

  const scrollOffset = useMemo(() => {
    if (cursorIndex < VISIBLE_LINES) return 0;
    return cursorIndex - VISIBLE_LINES + 1;
  }, [cursorIndex]);

  const visibleChildren = children.slice(scrollOffset, scrollOffset + VISIBLE_LINES);

  const handleInsertUsb = () => {
    if (!usbEjected || usbInserted) return;
    onAction("insert_usb", {});
  };

  const handleVote = (action, payload = {}) => {
    setPendingAction(`${action}:${JSON.stringify(payload)}`);
    onAction(action, payload);
  };

  const canCopy = selectedItem?.isProof && !uploadedSet.has(selectedItem.path);
  const canEnter = selectedItem?.type === "folder";
  const canPaste = clipboard !== null
    && currentPath === "/CONFERENCE"
    && !uploadedSet.has(clipboard.filePath);

  const uploadCount = uploadedPaths.length;
  const totalProofs = proofPaths.length;

  // Hint contextuel pour le presse-papiers


  return (
    <div className="flex flex-col h-full w-full select-none" style={{ fontFamily: "'Courier New', monospace" }}>

      {/* ═══ ÉCRAN ═══ */}
      <div
        className="flex-1 rounded-t-lg overflow-hidden relative"
        style={{
          background: voteFlash?.success ? "rgba(0,40,0,1)" : "#000e00",
          border: "2px solid #1a4a1a",
          boxShadow: "inset 0 0 60px rgba(0,255,0,0.05), 0 0 30px rgba(0,200,0,0.15)",
          transition: "background 0.1s",
        }}
      >
        {/* Scanlines */}
        <div className="absolute inset-0 pointer-events-none z-10"
          style={{ backgroundImage: "repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.12) 2px, rgba(0,0,0,0.12) 4px)" }}
        />
        {voteFlash?.success && (
          <div className="absolute inset-0 z-20 pointer-events-none"
            style={{ background: "rgba(0,255,0,0.08)" }} />
        )}

        <div className="relative z-5 p-3 h-full flex flex-col">

          {/* Header */}
          <div className="flex items-center justify-between mb-2 pb-2"
            style={{ borderBottom: "1px solid #0a3a0a" }}>
            <div style={{ color: "#00cc00", fontSize: "10px", letterSpacing: "0.2em" }}>
              SRV-01 :: TRANSFERT SÉCURISÉ
            </div>
            <div className="flex items-center gap-3">
              <span style={{ color: "#006600", fontSize: "9px" }}>
                CLÉ USB:{" "}
                {usbInserted
                  ? <span style={{ color: "#00ff00" }}>MONTÉE</span>
                  : <span style={{ color: "#333" }}>HORS LIGNE</span>}
              </span>
              <span style={{ color: "#006600", fontSize: "9px" }}>
                CONF:{" "}
                <span style={{ color: uploadCount === totalProofs ? "#00ff00" : "#ffaa00" }}>
                  {uploadCount}/{totalProofs}
                </span>
              </span>
            </div>
          </div>

          {/* ── BANDEAU PRESSE-PAPIERS — toujours visible, accent violet quand actif ── */}
          <div style={{
            marginBottom: "8px",
            padding: "5px 10px",
            borderRadius: "3px",
            border: clipboard ? "1px solid rgba(170,100,255,0.55)" : "1px solid #0c1a0c",
            background: clipboard ? "rgba(70,15,110,0.3)" : "rgba(0,0,0,0.15)",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            transition: "all 0.2s",
          }}>
            <span style={{
              fontSize: "9px", letterSpacing: "0.12em",
              color: clipboard ? "rgba(180,120,255,0.65)" : "#152015",
              flexShrink: 0,
            }}>
              PRESSE-PAPIERS
            </span>
            {clipboard ? (
              <>
                <span style={{
                  fontSize: "11px", fontWeight: "bold",
                  color: "#cc88ff",
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  flex: 1,
                }}>
                  ▶ {clipboard.fileName}
                </span>
              </>
            ) : (
              <span style={{ fontSize: "9px", color: "#152015", flex: 1 }}>
                VIDE — sélectionnez une preuve (★) et votez COPIER
              </span>
            )}
          </div>

          {!usbInserted ? (
            <div className="flex-1 flex flex-col items-center justify-center gap-3">
              <div style={{ color: "#003300", fontSize: "11px", textAlign: "center", lineHeight: "1.8" }}>
                {["SYSTÈME PRÊT", "EN ATTENTE DU PÉRIPHÉRIQUE...", "PORT E: EN VEILLE", "", ""].map((l, i) => (
                  <div key={i} style={{ opacity: 0.2 + i * 0.15 }}>{l || " "}</div>
                ))}
              </div>
              <div style={{ color: "#00aa00", fontSize: "11px", letterSpacing: "0.15em" }} className="animate-pulse">
                ► INSÉRER LA CLÉ USB POUR COMMENCER
              </div>
            </div>
          ) : allUploaded ? (
            <div className="flex-1 flex flex-col items-center justify-center gap-4">
              <div style={{ color: "#00ff00", fontSize: "12px", letterSpacing: "0.15em", lineHeight: "2", textAlign: "center" }}>
                {"█".repeat(26)}<br />
                {"█  TRANSFERT TERMINÉ      █"}<br />
                {`█  ${totalProofs}/${totalProofs} FICHIERS → /CONFERENCE  █`}<br />
                {"█".repeat(26)}
              </div>
              <div style={{ color: "#00aa00", fontSize: "9px" }}>TOUTES LES PREUVES SÉCURISÉES</div>
            </div>
          ) : (
            <div className="flex-1 flex flex-col overflow-hidden">

              {/* Breadcrumb */}
              <div style={{ color: "#004400", fontSize: "9px", marginBottom: "4px" }}>
                {">"} /{currentPath.split("/").filter(Boolean).join("/")}
              </div>

              {/* En-têtes colonnes */}
              <div className="flex gap-2 mb-1"
                style={{ color: "#004400", fontSize: "9px", borderBottom: "1px dashed #002200", paddingBottom: "2px" }}>
                <span style={{ width: "16px" }}> </span>
                <span style={{ flex: 1 }}>NOM</span>
                <span style={{ width: "44px", textAlign: "right" }}>TYPE</span>
                <span style={{ width: "52px", textAlign: "right" }}>ÉTAT</span>
              </div>

              {/* Entrée ".." */}
              {canGoUp && (
                <div style={{ color: "#004400", fontSize: "11px", padding: "1px 0", paddingLeft: "18px" }}>
                  ..  <span style={{ fontSize: "9px", opacity: 0.5 }}>[dossier parent]</span>
                </div>
              )}

              {/* Fichiers */}
              <div className="flex-1 overflow-hidden">
                {visibleChildren.map((item, idx) => {
                  const absIdx = idx + scrollOffset;
                  const isCursor = absIdx === cursorIndex;
                  const isUploaded = uploadedSet.has(`/USB_DEVICE/${item.name}`) || uploadedSet.has(item.path);
                  // Fichier actuellement dans le presse-papiers
                  const isInClipboard = !!clipboard && clipboard.fileName === item.name;

                  let color = "#004d00";
                  if (item.type === "folder") color = "#0088cc";
                  if (item.isProof && !isUploaded) color = "#ccaa00";
                  if (item.isProof && isUploaded) color = "#00bb66";
                  if (item.isDestination) color = "#0088cc";
                  if (isInClipboard) color = "#bb88ff";

                  return (
                    <div key={item.path}
                      style={{
                        display: "flex", gap: "8px", alignItems: "center",
                        padding: "1px 2px", fontSize: "11px",
                        background: isInClipboard && !isCursor
                          ? "rgba(100,30,160,0.18)"
                          : isCursor ? "rgba(0,255,0,0.1)" : "transparent",
                        color: isCursor
                          ? (isInClipboard ? "#dd99ff" : "#00ff00")
                          : color,
                        borderLeft: isInClipboard
                          ? "2px solid rgba(180,100,255,0.7)"
                          : isCursor ? "2px solid #00ff00" : "2px solid transparent",
                      }}
                    >
                      <span style={{ width: "14px", fontSize: "10px", flexShrink: 0 }}>
                        {isInClipboard ? "▶" : item.type === "folder" ? "▶" : item.isProof ? "★" : "·"}
                      </span>
                      <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {item.name}{item.type === "folder" ? "/" : ""}
                      </span>
                      <span style={{ width: "42px", textAlign: "right", fontSize: "9px", opacity: 0.6 }}>
                        {item.type === "folder" ? "REP" : item.name.split(".").pop()?.toUpperCase()}
                      </span>
                      <span style={{ width: "52px", textAlign: "right", fontSize: "9px" }}>
                        {isInClipboard
                          ? <span style={{ color: "#cc88ff", fontWeight: "bold" }}>COPIÉ</span>
                          : isUploaded
                            ? <span style={{ color: "#00ff88" }}>✓ OK</span>
                            : item.isProof
                              ? <span style={{ color: "#ccaa00" }}>★ COPIER</span>
                              : <span style={{ opacity: 0.2 }}>---</span>
                        }
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Barre de vote */}
              {voteState && (
                <div className="mt-2 pt-2" style={{ borderTop: "1px dashed #002200" }}>
                  <div className="flex items-center justify-between mb-1">
                    <span style={{ color: "#ffcc00", fontSize: "9px", letterSpacing: "0.1em" }}>
                      VOTE:{" "}
                      {voteState.action === "nav" ? `NAVIGATION ${voteState.payload?.dir === "up" ? "↑" : "↓"}`
                        : voteState.action === "enter" ? (voteState.payload?.path === ".." ? "RETOUR" : "OUVRIR")
                          : voteState.action === "copy" ? `COPIER ${voteState.payload?.fileName}`
                            : voteState.action === "paste" ? "COLLER"
                              : voteState.action.toUpperCase()}
                    </span>
                    <span style={{ color: "#ffcc00", fontSize: "9px" }}>
                      {voteState.voters.length}/{Math.min(voteState.threshold, playerCount)}
                    </span>
                  </div>
                  <div className="flex gap-1 mb-1">
                    {Array.from({ length: playerCount }, (_, i) => {
                      const v = String(i + 1);
                      const hasVoted = voteState.voters.includes(v);
                      return (
                        <div key={v} style={{
                          width: "18px", height: "18px", borderRadius: "3px",
                          background: hasVoted ? getColorForVariant(v) : "#111",
                          border: `1px solid ${hasVoted ? getColorForVariant(v) : "#222"}`,
                          boxShadow: hasVoted ? `0 0 6px ${getColorForVariant(v)}` : "none",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: "8px", color: hasVoted ? "#000" : "#333",
                          fontWeight: "bold", transition: "all 0.15s",
                        }}>
                          {v}
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ height: "3px", background: "#111", borderRadius: "2px", overflow: "hidden" }}>
                    <div style={{
                      height: "100%", width: `${voteProgress}%`,
                      background: "linear-gradient(90deg, #ffcc00, #ffee44)",
                      transition: "width 0.2s",
                    }} />
                  </div>
                </div>
              )}

              {/* Statusbar */}
              <div className="mt-1 flex justify-between" style={{ color: "#003300", fontSize: "9px" }}>
                <span>{children.length} éléments</span>
                <span>curseur {cursorIndex + 1}/{Math.max(children.length, 1)}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ═══ PANNEAU PHYSIQUE ═══ */}
      <div className="rounded-b-lg p-4"
        style={{
          background: "linear-gradient(180deg, #1a1a2e 0%, #0f0f1a 100%)",
          border: "2px solid #1a4a1a", borderTop: "none",
        }}
      >
        {/* Port USB */}
        <div className="flex items-center gap-3 mb-4">
          <span style={{ color: "#333", fontSize: "9px", letterSpacing: "0.12em", width: "72px" }}>PORT USB-E</span>
          <button
            onClick={handleInsertUsb}
            disabled={!usbEjected || usbInserted}
            style={{
              display: "flex", alignItems: "center", gap: "8px",
              padding: "5px 12px", borderRadius: "4px", fontSize: "10px",
              letterSpacing: "0.1em", cursor: (!usbEjected || usbInserted) ? "not-allowed" : "pointer",
              opacity: (!usbEjected && !usbInserted) ? 0.35 : 1,
              background: usbInserted ? "#003300" : usbEjected ? "#1a3a1a" : "#111",
              border: `1px solid ${usbInserted ? "#00aa00" : usbEjected ? "#336633" : "#222"}`,
              color: usbInserted ? "#00cc00" : usbEjected ? "#559955" : "#333",
            }}
          >
            <div style={{
              width: "26px", height: "10px", borderRadius: "2px",
              background: usbInserted ? "#004400" : "#0a0a0a",
              border: "1px solid #333", position: "relative", overflow: "hidden",
            }}>
              {usbInserted && (
                <div style={{ position: "absolute", inset: "2px", background: "#006600", borderRadius: "1px" }} />
              )}
            </div>
            {usbInserted ? "CLÉ USB MONTÉE" : usbEjected ? "INSÉRER LA CLÉ USB" : "CLÉ USB NON DISPONIBLE"}
          </button>
          {!usbEjected && !usbInserted && (
            <span style={{ color: "#552222", fontSize: "9px" }}>⚠ Éjecter depuis l'ordi d'abord</span>
          )}
        </div>

        {/* Boutons */}
        <div className="flex items-start gap-3">
          <div className="flex flex-col gap-1">
            <div style={{ color: "#333", fontSize: "8px", letterSpacing: "0.1em", textAlign: "center", marginBottom: "2px" }}>
              NAV [{voteThresholds.nav} votes]
            </div>
            <VoteButton label="▲ HAUT" actionKey={`nav:{"dir":"up"}`} voteState={voteState} myVariant={myVariant} playerCount={playerCount} disabled={!usbInserted || allUploaded} onClick={() => handleVote("nav", { dir: "up" })} />
            <VoteButton label="▼ BAS" actionKey={`nav:{"dir":"down"}`} voteState={voteState} myVariant={myVariant} playerCount={playerCount} disabled={!usbInserted || allUploaded} onClick={() => handleVote("nav", { dir: "down" })} />
          </div>

          <div style={{ width: "1px", height: "70px", background: "#1a2a1a", margin: "20px 4px 0" }} />

          <div className="flex flex-col gap-1">
            <div style={{ color: "#333", fontSize: "8px", letterSpacing: "0.1em", textAlign: "center", marginBottom: "2px" }}>
              NAVIGATION [{voteThresholds.enter} votes]
            </div>
            <VoteButton label="↩ RETOUR" actionKey={`enter:{"path":".."}`} voteState={voteState} myVariant={myVariant} playerCount={playerCount} disabled={!usbInserted || !canGoUp || allUploaded} onClick={() => handleVote("enter", { path: ".." })} />
            <VoteButton label="▶ OUVRIR" actionKey={selectedItem ? `enter:${JSON.stringify({ path: selectedItem.path })}` : ""} voteState={voteState} myVariant={myVariant} playerCount={playerCount} disabled={!usbInserted || !canEnter || allUploaded} onClick={() => selectedItem && handleVote("enter", { path: selectedItem.path })} />
          </div>

          <div style={{ width: "1px", height: "70px", background: "#1a2a1a", margin: "20px 4px 0" }} />

          <div className="flex flex-col gap-1">
            <div style={{ color: "#886600", fontSize: "8px", letterSpacing: "0.1em", textAlign: "center", marginBottom: "2px" }}>
              PRESSE-PAPIERS [{voteThresholds.copy} votes]
            </div>
            <VoteButton
              label="★ COPIER"
              actionKey={selectedItem?.isProof && !uploadedSet.has(selectedItem.path)
                ? `copy:${JSON.stringify({ filePath: selectedItem.path, fileName: selectedItem.name })}`
                : ""}
              voteState={voteState} myVariant={myVariant} playerCount={playerCount}
              disabled={!usbInserted || !canCopy || allUploaded}
              onClick={() => selectedItem?.isProof && handleVote("copy", { filePath: selectedItem.path, fileName: selectedItem.name })}
              accent
            />
            <VoteButton
              label={clipboard ? `▶ COLLER "${clipboard.fileName}"` : "▶ COLLER"}
              actionKey={`paste:{}`}
              voteState={voteState} myVariant={myVariant} playerCount={playerCount}
              disabled={!usbInserted || !canPaste || allUploaded}
              onClick={() => canPaste && handleVote("paste", {})}
              accentGreen
            />
          </div>

          {/* Progress LEDs */}
          <div className="ml-auto flex flex-col items-end gap-2 mt-1">
            <div style={{ color: "#333", fontSize: "8px", letterSpacing: "0.1em" }}>/CONFERENCE</div>
            <div className="flex gap-1">
              {proofPaths.map((p, i) => {
                const done = uploadedSet.has(p);
                return (
                  <div key={i} style={{
                    width: "10px", height: "22px", borderRadius: "2px",
                    background: done ? "#00aa44" : "#0a0a0a",
                    border: `1px solid ${done ? "#00ff66" : "#1a1a1a"}`,
                    boxShadow: done ? "0 0 5px #00ff44" : "none",
                    transition: "all 0.3s",
                  }} />
                );
              })}
            </div>
            <div style={{ color: uploadCount === totalProofs ? "#00ff00" : "#333", fontSize: "8px" }}>
              {uploadCount}/{totalProofs}
            </div>
          </div>
        </div>

        {process.env.NODE_ENV !== "production" && (
          <div style={{ marginTop: "12px", paddingTop: "10px", borderTop: "1px dashed #1a1a1a" }}>
            <button
              onClick={() => onDebugForceUpload?.()}
              disabled={allUploaded}
              style={{
                width: "100%", padding: "5px 10px", fontSize: "9px",
                letterSpacing: "0.12em", borderRadius: "4px",
                cursor: allUploaded ? "not-allowed" : "pointer",
                opacity: allUploaded ? 0.3 : 1,
                background: "#0a0a1a", border: "1px dashed #333", color: "#555",
                textTransform: "uppercase", transition: "all 0.1s",
              }}
              onMouseEnter={(e) => { if (!allUploaded) { e.currentTarget.style.background = "#0f0f2a"; e.currentTarget.style.color = "#8888ff"; e.currentTarget.style.borderColor = "#444"; } }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "#0a0a1a"; e.currentTarget.style.color = "#555"; e.currentTarget.style.borderColor = "#333"; }}
            >
              [DEBUG] forcer upload toutes les preuves
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function VoteButton({ label, actionKey, voteState, myVariant, playerCount, disabled, onClick, accent, accentGreen }) {
  const isActiveVote = voteState && `${voteState.action}:${JSON.stringify(voteState.payload)}` === actionKey;
  const iVoted = isActiveVote && voteState.voters.includes(String(myVariant));
  const [pressed, setPressed] = useState(false);

  const activeColor = accentGreen ? "#00cc44" : accent ? "#ffcc00" : "#00cc44";
  const borderActive = accentGreen ? "#00aa44" : accent ? "#ffaa00" : "#00aa44";
  const borderIdle = accentGreen ? "#1a3a1a" : accent ? "#443300" : "#2a3a2a";
  const colorIdle = accentGreen ? "#336633" : accent ? "#886600" : "#557755";

  return (
    <button
      disabled={disabled}
      onMouseDown={() => !disabled && setPressed(true)}
      onMouseUp={() => { setPressed(false); if (!disabled) onClick?.(); }}
      onMouseLeave={() => setPressed(false)}
      style={{
        padding: "5px 10px", fontSize: "9px", letterSpacing: "0.1em",
        borderRadius: "4px", cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.3 : 1,
        transform: pressed ? "translateY(1px)" : "translateY(0)",
        transition: "all 0.05s",
        background: iVoted
          ? accentGreen ? "#003a1a" : accent ? "#3a2a00" : "#0a2a0a"
          : accentGreen ? "#001a0a" : accent ? "#1a1200" : "#141e14",
        border: `1px solid ${iVoted ? borderActive : borderIdle}`,
        color: iVoted ? activeColor : colorIdle,
        boxShadow: iVoted ? `0 0 8px ${activeColor}44` : "inset 0 1px 0 rgba(255,255,255,0.03)",
        minWidth: "90px",
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: "6px",
      }}
    >
      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
      {isActiveVote && (
        <span style={{ fontSize: "8px", opacity: 0.8, flexShrink: 0 }}>
          {voteState.voters.length}/{Math.min(voteState.threshold, playerCount)}
        </span>
      )}
    </button>
  );
}